import { useEffect, useState } from "react";
import { getMcpStatus, setMcpEnabled, type McpStatus } from "../lib/api.ts";
import { toast } from "./Toast.tsx";

const MCP_URL = "https://mcp.currency-core.com/mcp";

/**
 * MCP server panel, shown on the Developer page next to API keys.
 *
 * Always visible so an org can discover the connector and copy the server URL.
 * Once the org has connected at least once it also shows MCP usage and an
 * enable/disable switch — disabling 403s the org's MCP key without revoking
 * anything. MCP requests count toward the org's overall usage; this surfaces
 * how much of it came through MCP.
 */
export function McpServerPanel({ orgId }: { orgId: string }) {
  const [status, setStatus] = useState<McpStatus | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    let active = true;
    void getMcpStatus(orgId)
      .then((s) => { if (active) { setStatus(s); setLoaded(true); } })
      .catch(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, [orgId]);

  async function toggle(next: boolean) {
    setSaving(true);
    try {
      await setMcpEnabled(orgId, next);
      setStatus((s) => (s ? { ...s, enabled: next } : s));
      toast(next ? "MCP access enabled." : "MCP access disabled.", true);
    } catch {
      toast("Couldn't update MCP access.");
    } finally {
      setSaving(false);
    }
  }

  function copyUrl() {
    void navigator.clipboard?.writeText(MCP_URL).then(
      () => toast("Server URL copied.", true),
      () => toast("Couldn't copy. Please copy it manually."),
    );
  }

  const connected = status?.connected ?? false;
  const enabled = status?.enabled ?? true;
  const pct = status && status.requests > 0
    ? Math.round((status.mcpRequests / status.requests) * 100)
    : 0;

  return (
    <div className="card mt rise">
      <div className="card-head">
        <h2>MCP server</h2>
        {loaded && (
          connected
            ? <span className="badge badge-accent" style={{ fontSize: "10px" }}>Connected</span>
            : <span className="badge badge-neutral" style={{ fontSize: "10px" }}>Not connected</span>
        )}
      </div>
      <div className="card-pad">
        <p className="muted" style={{ marginTop: 0 }}>
          Let AI assistants (Claude, Cursor, VS Code, Gemini) use this org's data over the
          Model Context Protocol. They sign in, pick this org, then call the same APIs, with usage
          and limits applied exactly like an API key.{" "}
          <a
            href="https://currency-core.com/docs/mcp"
            target="_blank"
            rel="noreferrer"
            style={{ color: "var(--accent-text)" }}
          >
            How to connect →
          </a>
        </p>

        <div className="secret-box">
          <code>{MCP_URL}</code>
          <button className="btn btn-sm" onClick={copyUrl} type="button">Copy</button>
        </div>

        {!loaded ? null : connected ? (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: "28px", flexWrap: "wrap", marginTop: "16px" }}>
              <Stat
                label="MCP requests"
                value={(status?.mcpRequests ?? 0).toLocaleString()}
                sub={`${pct}% of ${(status?.requests ?? 0).toLocaleString()} this period`}
              />
              {status?.connectedAt && (
                <Stat label="Connected since" value={formatDate(status.connectedAt)} />
              )}
              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "10px" }}>
                <span className="hint">{enabled ? "MCP access on" : "MCP access off"}</span>
                <label className="switch" aria-label="Enable MCP access">
                  <input
                    type="checkbox"
                    checked={enabled}
                    disabled={saving}
                    onChange={(e) => void toggle(e.target.checked)}
                  />
                  <span className="track" />
                  <span className="thumb" />
                </label>
              </div>
            </div>
            {!enabled && (
              <p className="muted" style={{ fontSize: "12.5px", marginTop: "12px", marginBottom: 0 }}>
                MCP access is off. Connected clients are blocked until you turn it back on. Nothing is revoked.
              </p>
            )}
          </>
        ) : (
          <p className="muted" style={{ fontSize: "13px", marginTop: "12px", marginBottom: 0 }}>
            No MCP client has connected yet. Once one does, you'll see its usage here and a switch to
            turn MCP access on or off.
          </p>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div>
      <div style={{ fontSize: "12px", color: "var(--text-subtle)" }}>{label}</div>
      <div style={{ fontSize: "20px", fontWeight: 600, color: "var(--text)" }}>{value}</div>
      {sub && <div style={{ fontSize: "11px", color: "var(--text-faint)" }}>{sub}</div>}
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}
